import {
  DeviceListDto,
  DeviceListLineDto,
  DeviceInfoDto,
  GroupListDto,
  GroupDto,
  GetUserDetailsDto,
  MyKeysPromiseValue,
  DownloadLogDto,
  DownloadHistoryDto,
  DeviceSettingsDto,
  HistoryDtos, TransferDto, TransferHistoryDto, DownloadableCheckDto
} from './interfaces';
import * as Devices from '../../../modules/devices';
import * as Orders from '../../../modules/orders';
import * as DTypes from '../../../modules/devicetypes';
import * as Customers from '../../../modules/customers';
import * as DownloadLogs from '../../../modules/dialogs/downloadLogs';
import { GroupCommon, UserInfo } from '../../../models';
import { ServerSettings } from '../../../contexts/settings.context';
import { Activity, ActivityHistoryData } from '../../../modules/dialogs/transfer';
import { IDownloadableCheck } from '../../../interfaces/iDownload';

export const MyKeysPromiseMap = <T extends {}>(val: MyKeysPromiseValue<T>) =>
  val as T;

export const mapDeviceListLineDto = (
  data: DeviceListLineDto
): Devices.Device => {
  return {
    manufactureId: data.manufactureId,
    serialNumber: data.serialNumber,
    serialPresentationLabel: data.serialPresentationLabel,
    numericalIdentity: data.numericalIdentity,
    deviceName: data.deviceName,
    deviceType: data.deviceType,
    isActive: data.isActive,
    orderNumber: data.orderNumber,
    owner: data.owner,
    userWithReadRights: data.userWithReadRights,
    availability: data.availability,
    customerDeviceNo: data.customerDeviceNo,
    showOrderNumber: data.showOrderNumber,
  };
};

export const mapDeviceListDto = (
  data: DeviceListDto
): Devices.SearchPageRes => {
  return {
    items: data.deviceDtos.map(mapDeviceListLineDto),
    total: data.totalCount,
    totalsByType: Devices.TotalsMap(data),
  };
};
export const mapHistoryDto = (data: HistoryDtos): Devices.Log => ({
  datetime: data.eventTime,
  eventMessage: data.eventMessage,
  userName: data.userName,
  customerName: data.customerName,
});

export const mapDeviceInfoDto = (
  data: DeviceInfoDto
): Devices.DeviceExtendedWithHistoricalData => {
  return {
    ...Devices.DeviceMap(data),
    ...Devices.DeviceExtensionMap(data),
    deviceName: data.longDeviceName,
    historicalLogs: {
      total: data.historyList.totalCount,
      logs: data.historyList.historyDtos.map(mapHistoryDto),
    },
  };
};

const mapGroupDto = (data: GroupDto): GroupCommon => {
  return {
    numDevices: data.itemCount,
    totalsByType: Devices.TotalsMap(data),
  };
};

export const mapOrderGroupDto = (data: GroupDto): Orders.Order => {
  return {
    ...mapGroupDto(data),
    orderNumber: data.description,
    invoiceDate: data.timestamp,
  };
};

export const mapDTypeGroupDto = (data: GroupDto): DTypes.DeviceType => {
  return {
    ...mapGroupDto(data),
    deviceName: data.description,
  };
};

export const mapCustomerGroupDto = (data: GroupDto): Customers.Customer => {
  return {
    ...mapGroupDto(data),
    owner: data.description,
  };
};

export const mapOrderGroupListDto = (
  data: GroupListDto
): Orders.SearchPageRes => {
  return {
    total: data.totalCount,
    items: data.groupListDtos.map(mapOrderGroupDto),
  };
};

export const mapDTypeGroupListDto = (
  data: GroupListDto
): DTypes.SearchPageResponse => {
  return {
    total: data.totalCount,
    items: data.groupListDtos.map(mapDTypeGroupDto),
  };
};

export const mapCustomerGroupListDto = (
  data: GroupListDto
): Customers.SearchPageRes => {
  return {
    total: data.totalCount,
    items: data.groupListDtos.map(mapCustomerGroupDto),
  };
};

export const mapGetUserDetailsDto = (userDetailsDto: GetUserDetailsDto) => {
  return {
    userId: userDetailsDto.userId,
    email: userDetailsDto.userEmail,
    customer: {
      customerId: userDetailsDto.customerId,
      customerName:
        userDetailsDto.customerDisplayName || userDetailsDto.customerName,
    },
  } as UserInfo;
};

export const mapDownloadCheckDto = (
  check: DownloadableCheckDto
): IDownloadableCheck => ({
  numDevicesInKem: check.numDevicesInKem,
  invalidDevices: check.invalidDevices,
});

export const mapDownloadLogDto = (
  log: DownloadLogDto
): DownloadLogs.DownloadLog => ({
  datetime: log.created,
  username: log.username,
  deviceCount: log.deviceCount,
});

export const mapDownloadHistoryDto = (
  history: DownloadHistoryDto
): DownloadLogs.DownloadHistory => {
  return {
    total: history.totalCount,
    logs: history.downloadHistoryDtos.map(mapDownloadLogDto),
  } as DownloadLogs.DownloadHistory;
};

export const mapDeviceSettingsDto = (
  data: DeviceSettingsDto
): ServerSettings => ({
  products: data.deviceTypes,
});


export const mapTransferDto = (
  tx: TransferDto
): Activity => ({
  ...tx,
  datetime: new Date(tx.transferDate)?.getFullYear() > 1 ? tx.transferDate : tx.requestDate
});

export const mapTransferHistoryDto = (
  history: TransferHistoryDto
): ActivityHistoryData => {
  return {
    total: history.totalCount,
    logs: history.transferDtos.map(mapTransferDto),
  };
};